package com.bikompanion.data.gbfs

/**
 * One bike system published as a GBFS feed.
 *
 * Only the discovery URL is ever hardcoded. Every other feed is looked up by name inside that
 * document, which is how GBFS is meant to be consumed — an operator is free to move a feed,
 * and a system's whole set of endpoints follows from this one line.
 */
internal data class GbfsSystem(
    /**
     * The id this network is known by inside the app.
     *
     * Deliberately CityBikes' ids rather than the feeds' own (`bike_barcelona`,
     * `nextbike_bs`): station details, the operator-app button and the on-disk cache are all
     * keyed by network id, so reusing them keeps those working under either data source.
     */
    val networkId: String,
    val discoveryUrl: String,
    /** Shown if the feed's `system_information` has no usable name. */
    val fallbackName: String,
    val city: String,
    val country: String,
)

/**
 * The systems the GBFS source covers.
 *
 * Barcelona's two dock-based operators. Adding a third is a line here plus whatever its feed
 * calls its bikes — the reader already handles GBFS 2.x and 3.0 side by side, which is what
 * these two forced: Bicing publishes 3.0, AMBici 2.3.
 */
internal val GbfsSystems = listOf(
    GbfsSystem(
        networkId = "bicing",
        discoveryUrl = "https://barcelona.publicbikesystem.net/customer/gbfs/v3.0/gbfs.json",
        fallbackName = "Bicing",
        city = "Barcelona",
        country = "ES",
    ),
    GbfsSystem(
        networkId = "ambici",
        // nextbike publishes no 3.0 feed for this system; the 2.3 one is what the operator
        // registers with MobilityData.
        discoveryUrl = "https://gbfs.nextbike.net/maps/gbfs/v2/nextbike_bs/gbfs.json",
        fallbackName = "AMBici",
        city = "Barcelona",
        country = "ES",
    ),
)
